import React, { useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Box } from '@chakra-ui/react';

export interface MentionTextareaRef {
  focus: () => void;
  getCaretPosition: () => number;
  setCaretToEnd: () => void;
  getElement: () => HTMLDivElement | null;
}

interface MentionTextareaProps {
  value: string;
  // eslint-disable-next-line no-unused-vars
  onValueChange: (value: string) => void;
  onKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
  placeholder?: string;
}

const MENTION_REGEX = /@\[([^\]]+)\]\(id:(\d+)\)/g;

/**
 * Extrai o valor raw (com @[Nome](id:X)) do conteudo do contentEditable.
 * Spans de mencao tem data-mention-id e data-mention-name.
 */
export const extractRawValue = (container: HTMLDivElement): string => {
  let raw = '';
  container.childNodes.forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      raw += node.textContent || '';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const mentionId = el.getAttribute('data-mention-id');
      const mentionName = el.getAttribute('data-mention-name');
      if (mentionId && mentionName) {
        raw += `@[${mentionName}](id:${mentionId})`;
      } else {
        raw += el.textContent || '';
      }
    }
  });
  return raw;
};

/**
 * Constroi o HTML do contentEditable a partir do valor raw.
 */
const buildHtml = (raw: string): string => {
  if (!raw) return '';
  let html = '';
  let lastIndex = 0;
  const regex = new RegExp(MENTION_REGEX.source, 'g');
  let match = regex.exec(raw);

  while (match !== null) {
    if (match.index > lastIndex) {
      html += escapeHtml(raw.slice(lastIndex, match.index));
    }
    const name = match[1];
    const id = match[2];
    html += `<span contenteditable="false" data-mention-id="${id}" data-mention-name="${escapeAttr(name)}" style="color:#2B6CB0;font-weight:600;background:#EBF8FF;border-radius:4px;padding:0 2px;user-select:all;">@${escapeHtml(name)}</span>`;
    lastIndex = match.index + match[0].length;
    match = regex.exec(raw);
  }

  if (lastIndex < raw.length) {
    html += escapeHtml(raw.slice(lastIndex));
  }

  return html;
};

const escapeHtml = (str: string) =>
  str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const escapeAttr = (str: string) => str.replace(/"/g, '&quot;');

/**
 * Retorna a posicao do cursor em termos de texto "visivel"
 * (contando o span de mencao inteiro como @Nome).
 */
const getCaretOffset = (container: HTMLDivElement): number => {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return 0;

  const range = sel.getRangeAt(0);
  const preRange = document.createRange();
  preRange.selectNodeContents(container);
  preRange.setEnd(range.startContainer, range.startOffset);

  // Contar chars percorrendo os nodes ate o ponto do cursor
  let offset = 0;
  const whatToShow = NodeFilter.SHOW_TEXT + NodeFilter.SHOW_ELEMENT;
  const iterator = document.createNodeIterator(container, whatToShow);
  let current = iterator.nextNode();

  while (current) {
    if (current === range.startContainer) {
      if (current.nodeType === Node.TEXT_NODE) {
        offset += range.startOffset;
      }
      break;
    }

    if (current.nodeType === Node.TEXT_NODE) {
      // Verificar se o pai eh um mention span
      const parent = current.parentElement;
      if (parent && parent.getAttribute('data-mention-id') && parent.parentElement === container) {
        // Nao contamos texto dentro de mention aqui, contamos o mention inteiro quando vemos o element node
      } else if (current.parentElement === container) {
        offset += (current.textContent || '').length;
      }
    } else if (current.nodeType === Node.ELEMENT_NODE) {
      const el = current as HTMLElement;
      if (el.getAttribute('data-mention-id') && el.parentElement === container) {
        const mentionName = el.getAttribute('data-mention-name') || '';
        offset += `@${mentionName}`.length;
      }
    }

    current = iterator.nextNode();
  }

  return offset;
};

export const MentionTextarea = forwardRef<MentionTextareaRef, MentionTextareaProps>(
  ({ value, onValueChange, onKeyDown, placeholder }, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    // Começa vazio: montar com texto (ex.: voltar à aba Comentários) já o mostra no editor.
    const lastRawRef = useRef('');
    const isComposingRef = useRef(false);

    useImperativeHandle(ref, () => ({
      focus: () => {
        const el = editorRef.current;
        if (el) {
          el.focus();
          // Cursor no final
          const sel = window.getSelection();
          if (sel) {
            sel.selectAllChildren(el);
            sel.collapseToEnd();
          }
        }
      },
      getCaretPosition: () => {
        const el = editorRef.current;
        if (!el) return 0;
        return getCaretOffset(el);
      },
      setCaretToEnd: () => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        const sel = window.getSelection();
        if (sel) {
          sel.selectAllChildren(el);
          sel.collapseToEnd();
        }
      },
      getElement: () => editorRef.current,
    }));

    // Sincronizar conteudo quando o value externo muda (ex: insertMention)
    useEffect(() => {
      const el = editorRef.current;
      if (!el) return;
      if (value !== lastRawRef.current) {
        lastRawRef.current = value;
        el.innerHTML = buildHtml(value);
        // Colocar cursor no final
        const sel = window.getSelection();
        if (sel && el.childNodes.length > 0) {
          sel.selectAllChildren(el);
          sel.collapseToEnd();
        }
      }
    }, [value]);

    const handleInput = useCallback(() => {
      if (isComposingRef.current) return;
      const el = editorRef.current;
      if (!el) return;

      const raw = extractRawValue(el);
      lastRawRef.current = raw;
      onValueChange(raw);
    }, [onValueChange]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        onKeyDown(e);
      },
      [onKeyDown],
    );

    const showPlaceholder = !value;

    return (
      <Box position="relative">
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => {
            isComposingRef.current = true;
          }}
          onCompositionEnd={() => {
            isComposingRef.current = false;
            handleInput();
          }}
          role="textbox"
          tabIndex={0}
          minH="52px"
          w="100%"
          fontSize="13px"
          lineHeight="1.375"
          p="8px 44px 8px 16px"
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.200"
          outline="none"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
          overflowY="auto"
          cursor="text"
          _focus={{
            borderColor: 'blue.400',
            boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.1)',
          }}
          _empty={
            showPlaceholder
              ? {
                  _before: {
                    content: `"${placeholder || ''}"`,
                    color: 'gray.400',
                    pointerEvents: 'none',
                  },
                }
              : undefined
          }
          sx={{
            '& [data-mention-id]': {
              userSelect: 'all',
            },
          }}
        />
      </Box>
    );
  },
);

MentionTextarea.displayName = 'MentionTextarea';
